/**
 * Billing-period status rendering (FE4 / ENG-5450).
 *
 * Status → label + tone, and the caption that sits under the pill. The pill
 * chrome and the tone vocabulary itself live in `TonePill.tsx`, shared with the
 * expert arm's payment pills.
 *
 * A FAILED or VOIDED row pairs the pill with a caption underneath (the
 * HoursTab auto-approve caption pattern): direction, not mood — what happened
 * and what, if anything, resolves it. The retry ACTION never renders here;
 * remediation is the operator's (ENG-5630) and this surface is read-only.
 *
 * `failureReason` is an open server vocabulary — unknown values render the
 * bare status pill rather than crashing or inventing copy.
 */
import { EngagementBillingPeriodStatus, EngagementFundingMethod } from '@/__generated__/graphql';

import type { BillingPeriodRow } from '../viewModel';
import TonePill, { TONES, type Tone } from './TonePill';

/** Exported for the status table test — label and tone in one derivation. */
export function periodStatusDisplay(row: BillingPeriodRow): { label: string; tone: Tone } {
  const credits = row.fundingMethod === EngagementFundingMethod.Credits;
  switch (row.status) {
    case EngagementBillingPeriodStatus.Settled:
      return { label: credits ? 'Drawn' : 'Paid', tone: TONES.moved };
    case EngagementBillingPeriodStatus.Pending:
      if (credits) return { label: 'Pending', tone: TONES.working };
      return row.invoiced
        ? { label: 'Invoiced', tone: TONES.awaiting }
        : { label: 'Awaiting invoice', tone: TONES.working };
    case EngagementBillingPeriodStatus.Skipped:
      return { label: 'No hours', tone: TONES.quiet };
    case EngagementBillingPeriodStatus.Voided:
      return { label: 'Voided', tone: TONES.quiet };
    case EngagementBillingPeriodStatus.Failed:
      return { label: 'Failed', tone: TONES.failed };
    default:
      // The api maps unknown DB statuses to PENDING before they reach the
      // wire, so this only fires on a schema-ahead-of-webapp skew.
      return { label: 'Pending', tone: TONES.working };
  }
}

/**
 * The caption under a FAILED / VOIDED pill. Copy states the cause and the real
 * path out; the two auto-redriven reasons say so, the human-needed ones point
 * at support, and `credit_shortfall` names the balance without promising a
 * retry no surface offers yet.
 */
export function periodStatusCaption(row: BillingPeriodRow): string | null {
  if (row.status === EngagementBillingPeriodStatus.Voided) {
    return 'The engagement closed before this period could be billed.';
  }
  if (row.status !== EngagementBillingPeriodStatus.Failed) return null;
  switch (row.failureReason) {
    case 'credit_shortfall':
      return 'The account’s credit balance couldn’t cover this period.';
    case 'billing_email_missing':
      return 'No billing email on file — invoicing retries automatically once one is set.';
    case 'billing_settings_missing':
      return 'Billing settings are incomplete — invoicing retries automatically once set.';
    case 'stripe_customer_email_conflict':
    case 'pricing_config_invalid':
      return 'This period needs attention from OnFrontiers — contact support.';
    default:
      return null;
  }
}

export function PeriodStatusCell({ row }: { row: BillingPeriodRow }): JSX.Element {
  const { label, tone } = periodStatusDisplay(row);
  const caption = periodStatusCaption(row);
  return (
    <span className="flex flex-col gap-6">
      <TonePill tone={tone}>{label}</TonePill>
      {caption ? (
        <span className="max-w-[240px] text-on-surface-muted body-2">{caption}</span>
      ) : null}
    </span>
  );
}
